import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getActivities,
  deleteActivity,
  getActivityCategories,
} from "../../../services/activityService";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AddActivity from "./AddActivity";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierPageLoading,
} from "../../common/supplierPageUi";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useCloseOnSidebarNavigate } from "../../../hooks/useCloseOnSidebarNavigate";

const FILTER_SELECT_CLASS =
  "min-w-[8.5rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-400 focus:outline-none";

const parsePaginated = (payload) => {
  const paginated = payload?.data;
  if (!paginated) {
    return {
      items: [],
      current_page: 1,
      last_page: 1,
      total: 0,
      per_page: 10,
    };
  }
  const items = Array.isArray(paginated.data) ? paginated.data : [];
  return {
    items,
    current_page: paginated.current_page || 1,
    last_page: paginated.last_page || 1,
    total: paginated.total || 0,
    per_page: paginated.per_page || 10,
  };
};

const formatDuration = (minutesRaw) => {
  const mins = Number(minutesRaw);
  if (!Number.isFinite(mins) || mins <= 0) return "N/A";
  if (mins >= 60 && mins % 60 === 0) {
    const hrs = mins / 60;
    return `${hrs} Hrs`;
  }
  return `${mins} Mins`;
};

const Activities = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityToDeleteId, setActivityToDeleteId] = useState(null);
  const [activityToDeleteName, setActivityToDeleteName] = useState("");
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");

  const queryClient = useQueryClient();
  const location = useLocation();
  const pageMeta = SUPPLIER_PAGE_META.activities;

  const closeAddFlow = useCallback(() => {
    setAddActivityOpen(false);
    setSelectedActivityId(null);
  }, []);

  useEffect(() => {
    closeAddFlow();
  }, [location.pathname, closeAddFlow]);

  useCloseOnSidebarNavigate(closeAddFlow);

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (editId) {
      const idNum = Number(editId);
      if (Number.isFinite(idNum) && idNum > 0) {
        setSelectedActivityId(idNum);
        setAddActivityOpen(true);
      }
    }
  }, []);

  const filters = useMemo(() => {
    const f = {};
    if (debouncedSearchTerm) f.search = debouncedSearchTerm;
    if (filterCategory) f.activity_category_id = filterCategory;
    return f;
  }, [debouncedSearchTerm, filterCategory]);

  const {
    data: activitiesPayload,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["activities", currentPage, debouncedSearchTerm, filterCategory],
    queryFn: () => getActivities(currentPage, filters),
    keepPreviousData: true,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const { items: activities, current_page, last_page, total, per_page } = useMemo(
    () => parsePaginated(activitiesPayload),
    [activitiesPayload]
  );

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const { data: categoriesData } = useQuery({
    queryKey: ["activityCategories"],
    queryFn: getActivityCategories,
  });

  const categoryOptions = useMemo(() => {
    if (!categoriesData) return [];
    if (
      categoriesData.data?.data &&
      Array.isArray(categoriesData.data.data)
    ) {
      return categoriesData.data.data;
    }
    if (Array.isArray(categoriesData)) return categoriesData;
    if (Array.isArray(categoriesData.data)) return categoriesData.data;
    return [];
  }, [categoriesData]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleFilterCategoryChange = useCallback((e) => {
    setFilterCategory(e.target.value);
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilterCategory("");
    setCurrentPage(1);
  }, []);

  const deleteMutation = useMutation({
    mutationFn: deleteActivity,
    onSuccess: () => {
      queryClient.invalidateQueries(["activities"]);
      setDeleteDialogOpen(false);
      setActivityToDeleteId(null);
      setActivityToDeleteName("");
      setToasterMessage("Activity deleted successfully!");
      setToasterType("success");
      setShowToaster(true);
    },
    onError: (err) => {
      setToasterMessage(
        err?.response?.data?.message || "Failed to delete activity"
      );
      setToasterType("error");
      setShowToaster(true);
    },
  });

  const handleAddActivity = () => {
    setSelectedActivityId(null);
    setAddActivityOpen(true);
  };

  const handleEdit = (activity) => {
    setSelectedActivityId(activity.id);
    setAddActivityOpen(true);
  };

  const handleDelete = (id, name) => {
    setActivityToDeleteId(id);
    setActivityToDeleteName(name || "this activity");
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (activityToDeleteId) {
      deleteMutation.mutate(activityToDeleteId);
    }
  };

  const renderStatus = (activity) => {
    if (!activity.submitted_at) {
      return <span className="text-sm text-gray-500">Draft</span>;
    }
    if (activity.is_approved === 0) {
      return <span className="text-sm text-amber-700">Pending</span>;
    }
    if (activity.is_approved === 1) {
      return <span className="text-sm text-green-700">Approved</span>;
    }
    if (activity.is_approved === 2) {
      return <span className="text-sm text-red-600">Rejected</span>;
    }
    return <span className="text-sm text-gray-500">Unknown</span>;
  };

  if (addActivityOpen) {
    return (
      <AddActivity
        open={addActivityOpen}
        activityId={selectedActivityId}
        onClose={() => {
          setAddActivityOpen(false);
          setSelectedActivityId(null);
          queryClient.invalidateQueries(["activities"]);
        }}
      />
    );
  }

  const hasActiveFilters = searchTerm || filterCategory;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={
          <SupplierAddButton onClick={handleAddActivity}>Add Activity</SupplierAddButton>
        }
      />

      <AdminPageCard>
        <AdminPageCardToolbar className="border-b border-gray-100 bg-white px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <TableSearchInput
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search activities..."
                className="w-full sm:max-w-[220px]"
              />
              <select
                value={filterCategory}
                onChange={handleFilterCategoryChange}
                className={FILTER_SELECT_CLASS}
                aria-label="Category"
              >
                <option value="">All categories</option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                <span className="font-semibold text-gray-900">{total}</span>{" "}
                {total === 1 ? "activity" : "activities"}
              </span>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-medium text-gray-600 transition hover:text-gray-900"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </AdminPageCardToolbar>

        {isLoading && !isSearching ? (
          <SupplierPageLoading message="Loading activities..." />
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading activities
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh>Activity</AdminTableTh>
              <AdminTableTh>Category</AdminTableTh>
              <AdminTableTh>Duration</AdminTableTh>
              <AdminTableTh>Location</AdminTableTh>
              <AdminTableTh>Status</AdminTableTh>
              <AdminTableTh>Created</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {activities.length === 0 ? (
                <TableEmptyRow
                  colSpan={7}
                  {...getTableEmptyProps("activities", {
                    onAdd: handleAddActivity,
                    hasActiveFilters: !!(searchTerm || filterCategory),
                    onClearFilters: clearFilters,
                  })}
                />
              ) : (
                activities.map((activity) => (
                  <AdminTableRow key={activity.id}>
                    <AdminTableCell className="font-medium text-gray-900">
                      {activity.name || "Untitled Activity"}
                    </AdminTableCell>
                    <AdminTableCell className="text-sm text-gray-600">
                      {activity.activity_category?.name ||
                        activity.activityCategory?.name ||
                        "N/A"}
                    </AdminTableCell>
                    <AdminTableCell>{formatDuration(activity.duration)}</AdminTableCell>
                    <AdminTableCell>
                      {activity.location || activity.address || "N/A"}
                    </AdminTableCell>
                    <AdminTableCell>{renderStatus(activity)}</AdminTableCell>
                    <AdminTableCell className="text-sm text-gray-500">
                      {formatListingDateTime(activity.created_at)}
                    </AdminTableCell>
                    <TableActionsCell>
                      <EditActionButton
                        showLabel={false}
                        onClick={() => handleEdit(activity)}
                      />
                      <DeleteActionButton
                        showLabel={false}
                        onClick={() => handleDelete(activity.id, activity.name)}
                      />
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setActivityToDeleteId(null);
          setActivityToDeleteName("");
        }}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Activity
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={confirmDelete}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setDeleteDialogOpen(false);
          setActivityToDeleteId(null);
          setActivityToDeleteName("");
        }}
        isLoading={deleteMutation.isPending}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete &quot;{activityToDeleteName}
            &quot;? This action cannot be undone.
          </p>
        </div>
      </Dialog>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Activities;
